import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, query, orderBy } from 'firebase/firestore';
import { db, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { FiPlus, FiSettings, FiUsers, FiMapPin, FiCheck, FiX, FiDownload, FiHash, FiTrash2 } from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import QRCode from 'qrcode';
import './BlockSetup.css';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const BlockSetup = () => {
  // Get user authentication
  const { user } = useAuth();
  
  const [activeTab, setActiveTab] = useState('create');
  const [franchises, setFranchises] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Backend functions
  const getBlocksFunction = httpsCallable(functions, 'getBlocks');
  const createBlockFunction = httpsCallable(functions, 'createBlock');
  const assignBlockToFranchiseFunction = httpsCallable(functions, 'assignBlockToFranchise');

  // Create Block Form State
  const [createForm, setCreateForm] = useState({
    serial: '',
    name: '',
    location: '',
    address: '',
    assignedFranchise: ''
  });

  // Drag and Drop State
  const [draggedBlock, setDraggedBlock] = useState(null);
  const [dragOverFranchise, setDragOverFranchise] = useState(null);

  // QR Generation State
  const [selectedBlockForQR, setSelectedBlockForQR] = useState('');
  const [generatingQR, setGeneratingQR] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');

  // Delete Block State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [blockToDelete, setBlockToDelete] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);



  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      console.log('BlockSetup: Fetching blocks data...');
      
      // Use centralized API service instead of manual fetch
      const data = await api.block.getBlocksWithStats();
      
      console.log('BlockSetup: API response:', data);
      console.log('BlockSetup: Data type:', typeof data);
      console.log('BlockSetup: Data keys:', data ? Object.keys(data) : 'N/A');
      
      // Handle different response formats
      let blocksData = [];
      if (data && Array.isArray(data)) {
        // If data is directly an array
        blocksData = data;
      } else if (data && data.blocks && Array.isArray(data.blocks)) {
        // If data has blocks property
        blocksData = data.blocks;
      } else if (data && data.data && Array.isArray(data.data)) {
        // If data has data property
        blocksData = data.data;
      } else {
        // Fallback - log the structure
        console.warn('BlockSetup: Unexpected data structure:', data);
        blocksData = [];
      }
      
      console.log('BlockSetup: Blocks data:', blocksData);
      console.log('BlockSetup: Blocks count:', blocksData.length);
      
      setBlocks(blocksData);

      // Fetch franchises from Firestore
      const franchisesQuery = query(collection(db, 'franchises'), orderBy('name'));
      const franchisesSnapshot = await getDocs(franchisesQuery);
      const franchisesData = franchisesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFranchises(franchisesData);

    } catch (err) {
      console.error('BlockSetup: Error fetching data:', err);
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBlock = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Enhanced validation with specific field checks
    const missingFields = [];
    if (!createForm.serial?.trim()) missingFields.push('Serial number');
    if (!createForm.name?.trim()) missingFields.push('Name');
    
    if (missingFields.length > 0) {
      setError(`Please fill in the following required fields: ${missingFields.join(', ')}`);
      return;
    }

    try {
      const result = await createBlockFunction({
        serial: createForm.serial.trim(),
        name: createForm.name.trim(),
        location: createForm.location?.trim() || 'Unassigned',
        address: createForm.address?.trim() || '',
        assignedFranchise: createForm.assignedFranchise || null
      });

      if (result.data && result.data.success) {
        setSuccess(`Block "${createForm.name}" created successfully!`);
        setCreateForm({
          serial: '',
          name: '',
          location: '',
          address: '',
          assignedFranchise: ''
        });
        
        // Refresh blocks list
        fetchData();
      } else {
        throw new Error(result.data?.error || result.data?.message || 'Failed to create block');
      }
    } catch (err) {
      console.error('Create block error:', err);
      
      // Better error handling for Firebase Functions
      let errorMessage = 'Failed to create block';
      
      if (err.message.includes('already exists')) {
        errorMessage = 'A block with this serial number already exists. Please use a different serial number.';
      } else if (err.message.includes('missing data field')) {
        errorMessage = 'Server response error. Please try again.';
      } else if (err.message) {
        errorMessage = `Failed to create block: ${err.message}`;
      }
      
      setError(errorMessage);
    }
  };

  // Drag and Drop Functions
  const handleDragStart = (e, block) => {
    setDraggedBlock(block);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedBlock(null);
    setDragOverFranchise(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, franchiseId) => {
    e.preventDefault();
    setDragOverFranchise(franchiseId);
  };

  const handleDragLeave = (e) => {
    // Only clear if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverFranchise(null);
    }
  };

  const handleDrop = async (e, franchiseId) => {
    e.preventDefault();
    setDragOverFranchise(null);

    if (!draggedBlock) return;

    setError('');
    setSuccess('');

    try {
      // Call the assignment function
      const result = await assignBlockToFranchiseFunction({
        blockId: draggedBlock.id,
        franchiseId: franchiseId
      });

      if (result.data && result.data.success) {
        const franchise = franchises.find(f => f.id === franchiseId);
        setSuccess(`${draggedBlock.name} successfully assigned to ${franchise?.name}!`);
        
        // Refresh the data to show updated assignments
        fetchData();
      } else {
        throw new Error(result.data?.message || 'Failed to assign block');
      }
    } catch (err) {
      setError('Failed to assign block: ' + err.message);
    }

    setDraggedBlock(null);
  };

  const handleGenerateQR = async () => {
    if (!selectedBlockForQR) {
      setError('Please select a block to generate QR code');
      return;
    }

    setGeneratingQR(true);
    setError('');
    setSuccess('');

    try {
      const selectedBlock = blocks.find(block => block.id === selectedBlockForQR);
      if (!selectedBlock) {
        throw new Error('Selected block not found');
      }

      // Generate the checkout URL with dispenser_id parameter (always use HTTPS)
      const checkoutUrl = `https://${window.location.host}/checkout?dispenser_id=${selectedBlockForQR}`;
      
      // Generate QR code as data URL
      const qrDataUrl = await QRCode.toDataURL(checkoutUrl, {
        width: 400,
        margin: 2,
        color: {
          dark: '#1C3D49',
          light: '#FFFFFF'
        }
      });

      setQrCodeDataUrl(qrDataUrl);
      setSuccess(`QR code generated successfully for ${selectedBlock.name}!`);
      
    } catch (err) {
      setError('Failed to generate QR code: ' + err.message);
    } finally {
      setGeneratingQR(false);
    }
  };

  const handleDownloadQR = () => {
    if (!qrCodeDataUrl || !selectedBlockForQR) return;

    const selectedBlock = blocks.find(block => block.id === selectedBlockForQR);
    const fileName = `QR_Code_${selectedBlock?.name || selectedBlockForQR}.png`;

    // Create download link
    const link = document.createElement('a');
    link.href = qrCodeDataUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setSuccess(`QR code downloaded as ${fileName}!`);
  };

  // Delete Block Functions
  const handleDeleteBlock = (block) => {
    setBlockToDelete(block);
    setDeleteConfirmation('');
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    const expectedConfirmation = blockToDelete.name.substring(0, 3).toLowerCase();
    if (!blockToDelete || deleteConfirmation.toLowerCase() !== expectedConfirmation) {
      setError('Please type the first 3 letters of the block name to confirm deletion');
      return;
    }

    setDeleting(true);
    setError('');
    setSuccess('');

    try {
      console.log(`Attempting to delete block: ${blockToDelete.serial}`);
      
      const result = await api.block.deleteBlock(blockToDelete.serial, blockToDelete.serial);
      
      if (result.success) {
        setSuccess(`Block "${blockToDelete.name}" (${blockToDelete.serial}) has been permanently deleted`);
        setDeleteModalOpen(false);
        setBlockToDelete(null);
        setDeleteConfirmation('');
        
        // Refresh blocks list
        await fetchData();
      } else {
        throw new Error(result.error || 'Failed to delete block');
      }
    } catch (err) {
      console.error('Delete block error:', err);
      setError(`Failed to delete block: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setBlockToDelete(null);
    setDeleteConfirmation('');
    setError('');
  };

  // Calculate stats based on fetched data (with safety checks)
  const unassignedBlocks = (blocks || []).filter(block => !block.assignedFranchise || block.assignedFranchise === '');
  const assignedBlocks = (blocks || []).filter(block => block.assignedFranchise && block.assignedFranchise !== '');

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="block-setup">
      <div className="page-header">
        <div className="header-content">
          <FiSettings className="header-icon" />
          <div>
            <h1>Block Setup</h1>
            <p>Create new blocks and assign them to franchisees</p>
          </div>
        </div>
        <div className="header-stats">
          <div className="stat-card">
            <div className="stat-value">{blocks.length}</div>
            <div className="stat-label">Total Blocks</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{assignedBlocks.length}</div>
            <div className="stat-label">Assigned</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{unassignedBlocks.length}</div>
            <div className="stat-label">Unassigned</div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <FiX className="alert-icon" />
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <FiCheck className="alert-icon" />
          {success}
        </div>
      )}

      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'create' ? 'active' : ''}`}
          onClick={() => setActiveTab('create')}
        >
          <FiPlus className="tab-icon" />
          Create Block
        </button>

        <button 
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <FiMapPin className="tab-icon" />
          Block Overview
        </button>
        <button 
          className={`tab-button ${activeTab === 'qr' ? 'active' : ''}`}
          onClick={() => setActiveTab('qr')}
        >
          <FiHash className="tab-icon" />
          Generate QR
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'create' && (
          <div className="create-block-tab">
            <div className="form-section">
              <h3>Create New Block</h3>
              <form onSubmit={handleCreateBlock} className="block-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="serial">Block Serial Number *</label>
                    <input
                      type="text"
                      id="serial"
                      value={createForm.serial}
                      onChange={(e) => setCreateForm(prev => ({...prev, serial: e.target.value}))}
                      placeholder="e.g., BLK001"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="name">Block Name *</label>
                    <input
                      type="text"
                      id="name"
                      value={createForm.name}
                      onChange={(e) => setCreateForm(prev => ({...prev, name: e.target.value}))}
                      placeholder="e.g., Downtown Station A"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="location">Location</label>
                    <input
                      type="text"
                      id="location"
                      value={createForm.location}
                      onChange={(e) => setCreateForm(prev => ({...prev, location: e.target.value}))}
                      placeholder="e.g., Downtown District"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="address">Address</label>
                    <input
                      type="text"
                      id="address"
                      value={createForm.address}
                      onChange={(e) => setCreateForm(prev => ({...prev, address: e.target.value}))}
                      placeholder="e.g., 123 Main St, City, State"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="assignedFranchise">Assign to Franchise (Optional)</label>
                  <select
                    id="assignedFranchise"
                    value={createForm.assignedFranchise}
                    onChange={(e) => setCreateForm(prev => ({...prev, assignedFranchise: e.target.value}))}
                  >
                    <option value="">Leave Unassigned</option>
                    {franchises.map(franchise => (
                      <option key={franchise.id} value={franchise.id}>
                        {franchise.name}
                      </option>
                    ))}
                  </select>
                </div>

                <button type="submit" className="btn-primary">
                  <FiPlus className="btn-icon" />
                  Create Block
                </button>
              </form>
            </div>
          </div>
        )}



        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="overview-section">
              <h3>Block Assignment Overview</h3>
              <p className="drag-instructions">
                💡 <strong>Drag & Drop:</strong> Drag unassigned blocks to franchise sections to assign them
              </p>
              
              <div className="franchise-blocks">
                {franchises.map(franchise => {
                  const franchiseBlocks = blocks.filter(block => block.assignedFranchise === franchise.id);
                  const isDropTarget = dragOverFranchise === franchise.id;
                  
                  return (
                    <div 
                      key={franchise.id} 
                      className={`franchise-section ${isDropTarget ? 'drag-over' : ''}`}
                      onDragOver={handleDragOver}
                      onDragEnter={(e) => handleDragEnter(e, franchise.id)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, franchise.id)}
                    >
                      <div className="franchise-header">
                        <div className="franchise-info">
                        <h4>{franchise.name}</h4>
                          <span className="franchise-details">
                            {franchise.address && <span className="address">{franchise.address}</span>}
                            {franchise.timezone && (
                              <span className="timezone">
                                {franchise.timezone.split('/')[1]?.replace('_', ' ')}
                              </span>
                            )}
                          </span>
                        </div>
                        <span className="block-count">{franchiseBlocks.length} blocks</span>
                      </div>
                      
                      <div className="drop-zone">
                        {isDropTarget && draggedBlock && (
                          <div className="drop-indicator">
                            <FiPlus className="drop-icon" />
                            <span>Drop to assign {draggedBlock.name} to {franchise.name}</span>
                          </div>
                        )}
                      
                      {franchiseBlocks.length === 0 ? (
                          <div className="no-blocks-drop-zone">
                            <FiMapPin className="empty-icon" />
                            <p>No blocks assigned</p>
                            <p className="drop-hint">Drop blocks here to assign</p>
                          </div>
                      ) : (
                        <div className="blocks-list">
                          {franchiseBlocks.map(block => (
                            <div key={block.id} className="block-card assigned">
                              <div className="block-header">
                                  <span className="block-serial">{block.serial || block.id}</span>
                                <span className={`status-badge ${block.status?.toLowerCase()}`}>
                                  {block.status}
                                </span>
                                <button 
                                  className="delete-block-btn"
                                  onClick={() => handleDeleteBlock(block)}
                                  title="Delete Block"
                                >
                                  <FiTrash2 />
                                </button>
                              </div>
                              <h5>{block.name}</h5>
                              <p>{block.location}</p>
                                <div className="block-meta">
                                  {block.address && <p className="block-address">{block.address}</p>}
                                  {block.lastSeen && (
                                    <p className="last-seen">
                                      Last seen: {new Date(block.lastSeen).toLocaleDateString()}
                                    </p>
                                  )}
                                </div>
                            </div>
                          ))}
                        </div>
                      )}
                      </div>
                    </div>
                  );
                })}

                {unassignedBlocks.length > 0 && (
                  <div className="franchise-section unassigned-section">
                    <div className="franchise-header">
                      <div className="franchise-info">
                      <h4>Unassigned Blocks</h4>
                        <span className="franchise-details">
                          <span className="help-text">Drag these blocks to assign them to franchises</span>
                        </span>
                      </div>
                      <span className="block-count">{unassignedBlocks.length} blocks</span>
                    </div>
                    <div className="blocks-list">
                      {unassignedBlocks.map(block => (
                        <div 
                          key={block.id} 
                          className="block-card unassigned draggable"
                          draggable
                          onDragStart={(e) => handleDragStart(e, block)}
                          onDragEnd={handleDragEnd}
                        >
                          <div className="drag-handle">
                            <span className="drag-icon">⋮⋮</span>
                          </div>
                          <div className="block-content">
                          <div className="block-header">
                              <span className="block-serial">{block.serial || block.id}</span>
                            <span className="status-badge unassigned">Unassigned</span>
                            <button 
                              className="delete-block-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteBlock(block);
                              }}
                              title="Delete Block"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                          <h5>{block.name}</h5>
                          <p>{block.location}</p>
                            <div className="block-meta">
                              {block.address && <p className="block-address">{block.address}</p>}
                              {block.lastSeen && (
                                <p className="last-seen">
                                  Last seen: {new Date(block.lastSeen).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'qr' && (
          <div className="qr-generation-tab">
            <div className="qr-section">
              <h3>Generate QR Code</h3>
              <p>Generate a QR code that links directly to the checkout page for a specific block</p>
              
              <div className="qr-form">
                <div className="form-group">
                  <label htmlFor="selectedBlockForQR">Select Block</label>
                  <select
                    id="selectedBlockForQR"
                    value={selectedBlockForQR}
                    onChange={(e) => {
                      setSelectedBlockForQR(e.target.value);
                      setQrCodeDataUrl(''); // Clear previous QR code
                    }}
                  >
                    <option value="">Choose a block...</option>
                    {blocks.map(block => (
                      <option key={block.id} value={block.id}>
                        {block.name} ({block.serial || block.id}) - {block.location || 'No Location'}
                      </option>
                    ))}
                  </select>
                </div>

                <button 
                  onClick={handleGenerateQR}
                  className="btn-primary"
                  disabled={!selectedBlockForQR || generatingQR}
                >
                  <FiHash className="btn-icon" />
                  {generatingQR ? 'Generating...' : 'Generate QR Code'}
                </button>
              </div>

              {qrCodeDataUrl && (
                <div className="qr-result">
                  <div className="qr-preview">
                    <h4>QR Code Preview</h4>
                    <div className="qr-code-container">
                      <img src={qrCodeDataUrl} alt="Generated QR Code" className="qr-code-image" />
                    </div>
                    <div className="qr-info">
                      <p><strong>Block:</strong> {blocks.find(b => b.id === selectedBlockForQR)?.name}</p>
                      <p><strong>Serial:</strong> {blocks.find(b => b.id === selectedBlockForQR)?.serial || selectedBlockForQR}</p>
                      <p><strong>Checkout URL:</strong> {window.location.origin}/checkout?dispenser_id={selectedBlockForQR}</p>
                    </div>
                    <button 
                      onClick={handleDownloadQR}
                      className="btn-secondary qr-download-btn"
                    >
                      <FiDownload className="btn-icon" />
                      Download PNG
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && blockToDelete && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button 
              className="modal-close-btn"
              onClick={handleCancelDelete}
              disabled={deleting}
            >
              ×
            </button>
            
            <div className="modal-header">
              <h3>Delete Block?</h3>
            </div>
            
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{blockToDelete.name}</strong>?</p>
              <p>This action cannot be undone.</p>
              
              <div className="confirmation-input">
                <label htmlFor="delete-confirmation">
                  Type the first 3 letters of "{blockToDelete.name}" to confirm:
                </label>
                <input
                  type="text"
                  id="delete-confirmation"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder={blockToDelete.name.substring(0, 3)}
                  disabled={deleting}
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={handleCancelDelete}
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                className="btn-danger"
                onClick={handleConfirmDelete}
                disabled={deleting || deleteConfirmation.toLowerCase() !== blockToDelete.name.substring(0, 3).toLowerCase()}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlockSetup; 